/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {List, makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {BatteryIcon} from '@ion/dls/src/icons/BatteryIcon';
import {StatusIcon} from '@ion/dls/src/icons/StatusIcon';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {
  DLSTheme,
  IconSize,
  IconStatus,
  StatusIconSize
} from '@ion/dls/src/types/types';
import {getFormattedDate, resolveValue} from '@ion/dls/src/utils/ComponentUtils';
import {IonTagDetailsPanelProps} from './IonTagDetailsPanel';

const useStyles = makeStyles(({
  dls: {
    motion: {
      element: {
        smallExpansion: {
          close: closeSmallExpansion,
          open: openSmallExpansion
        }
      }
    }
  }
}: DLSTheme) => ({
  panelClosed : {
    transition : `transform ${closeSmallExpansion}`
  },
  panelOpen : {
    transform  : 'rotateZ(180deg)',
    transition : `transform ${openSmallExpansion}`
  },
  batteryIcon : {
    marginRight : '10px',
    marginTop   : '2px',
    float       : 'left'
  },
  row : {
    ...dlsTypography('body3'),
    lineHeight : '24px',
    padding    : '0px',
    color      : `${dlsColor('grey2')}`
  }
}));

export const IonTagCardListContent = ({device}: IonTagDetailsPanelProps) => {
  const classes = useStyles();
  const {tagReport} = device || {};
  const {
    deviceEsn,
    deviceDataConverted,
    eventTime,
    address,
    reportingDevice
  } = tagReport || {};
  const {
    street,
    city,
    state
  } = address || {};

  const inRange           = translate('tracking:ionTagDetail.inRange');
  const tagType           = deviceDataConverted?.pegBehavior.source.title;
  const battery           = deviceDataConverted?.accumulators.length - 1;
  const location          = address ? `${street} ${city}, ${state}` : '';
  const lastReported      = eventTime ? getFormattedDate(eventTime) : '';
  const gatewayDevice     = reportingDevice?.title;
  const batteryPercentage = deviceDataConverted?.accumulators?.[battery]?.value;

  return (
    <>
      <List>

        <CardItemRow label={translate('tracking:ionTagDetail.status')}
          value={
            <>
              <StatusIcon type={IconStatus.active} size={StatusIconSize.small} />
              <span className={classes.row}>
                {inRange}
              </span>
            </>
          }
          isCenterAligned={false}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />

        <CardItemRow label={translate('tracking:ionTagDetail.type')}
          value={resolveValue(tagType)}
          isCenterAligned={false}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />

        <CardItemRow label={translate('tracking:ionTagDetail.esn')}
          value={resolveValue(deviceEsn)}
          isCenterAligned={false}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />

        <CardItemRow label={translate('tracking:ionTagDetail.batteryLevel')}
          value={
            <>
              {batteryPercentage && (
                <span className={classes.batteryIcon}>
                  <BatteryIcon id='placeholder'
                    batteryPercentage={batteryPercentage}
                    size={IconSize.small}
                    color={dlsColor('caGreen')} />
                </span>
              )}
              <span className={classes.row}>
                {`${resolveValue(batteryPercentage)}${batteryPercentage ? '%' : ''}`}
              </span>
            </>
          }
          isCenterAligned={false}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />

        <CardItemRow label={translate('tracking:ionTagDetail.lastReported')}
          value={resolveValue(lastReported)}
          isCenterAligned={false}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />

        <CardItemRow label={translate('tracking:ionTagDetail.address')}
          value={resolveValue(location)}
          isCenterAligned={false}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />

        <CardItemRow label={translate('tracking:ionTagDetail.lastReportedTo')}
          value={resolveValue(gatewayDevice)}
          isCenterAligned={false}
          isBottomMargin={false}
          isTextRightAligned
          isCondensedVariant />

      </List>
    </>
  );
};

export default IonTagCardListContent;
